import { useId } from 'react';
import type { FieldError } from 'react-hook-form';

interface CheckboxOption {
  value: string;
  label: string;
}

interface FormCheckboxGroupProps {
  name: string;
  label: string;
  options: CheckboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: FieldError;
  required?: boolean;
  className?: string;
}

export function FormCheckboxGroup({
  name,
  label,
  options,
  value = [],
  onChange,
  error,
  required,
  className = '',
}: FormCheckboxGroupProps) {
  const id = useId();
  const handleChange = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionValue]);
    } else {
      onChange(value.filter((v) => v !== optionValue));
    }
  };

  return (
    <fieldset className={`space-y-3 ${className}`}>
      <legend className="block text-sm font-medium text-gray-900">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </legend>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <label
            key={option.value}
            className={`
              flex items-center gap-3 px-4 py-3
              bg-white border rounded-xl cursor-pointer
              transition-all duration-200
              ${
                value.includes(option.value)
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 hover:bg-gray-50'
              }
            `}
          >
            <input
              type="checkbox"
              name={name}
              value={option.value}
              checked={value.includes(option.value)}
              onChange={(e) => handleChange(option.value, e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-interactive"
              aria-describedby={error ? `${id}-error` : undefined}
            />
            <span className="text-gray-900 font-medium text-sm">
              {option.label}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p id={`${id}-error`} className="text-sm text-red-500">
          {error.message}
        </p>
      )}
    </fieldset>
  );
}
